import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, User, FloppyDisk } from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cached working-hours config loaded from /api/settings/working-hours.
// Falls back to BATI-BOYS hard-coded defaults if the endpoint is unavailable.
const HARD_CODED_DEFAULTS = {
  '0': { start: '08:00', end: '16:30', 'break': 30 },
  '1': { start: '08:00', end: '16:30', 'break': 30 },
  '2': { start: '08:00', end: '16:30', 'break': 30 },
  '3': { start: '08:00', end: '16:30', 'break': 30 },
  '4': { start: '08:00', end: '12:00', 'break': 0 },
  '5': { start: '08:00', end: '17:00', 'break': 60 },
  '6': { start: '08:00', end: '17:00', 'break': 60 },
};
let _workingHoursCache = null;
let _workingHoursPromise = null;
export const getWorkingHours = async () => {
  if (_workingHoursCache) return _workingHoursCache;
  if (!_workingHoursPromise) {
    _workingHoursPromise = axios.get(`${API_URL}/api/settings/working-hours`)
      .then(r => { _workingHoursCache = r.data?.hours || HARD_CODED_DEFAULTS; return _workingHoursCache; })
      .catch(() => { _workingHoursCache = HARD_CODED_DEFAULTS; return _workingHoursCache; });
  }
  return _workingHoursPromise;
};
export const invalidateWorkingHoursCache = () => { _workingHoursCache = null; _workingHoursPromise = null; };

// Synchronous lookup. Returns the cached value or the hard-coded default if not yet loaded.
const _lookupForDate = (isoDate) => {
  if (!isoDate) isoDate = format(new Date(), 'yyyy-MM-dd');
  const d = new Date(isoDate + 'T00:00:00');
  const dow = d.getDay(); // 0 Sun .. 6 Sat
  // Convert to Monday-first: Mon=0..Sun=6
  const idx = (dow + 6) % 7;
  const cfg = _workingHoursCache || HARD_CODED_DEFAULTS;
  const v = cfg[String(idx)] || HARD_CODED_DEFAULTS[String(idx)];
  return { start_time: v.start, end_time: v.end, break_duration: v['break'] };
};

/**
 * Default working hours per weekday — pulled from /api/settings/working-hours
 * (configurable per company), with BATI-BOYS defaults as fallback.
 */
export const defaultHoursForDate = (isoDate) => _lookupForDate(isoDate);

/**
 * Async variant: ensures the cache is populated before returning.
 * Use it from components that rely on the configured defaults.
 */
export const defaultHoursForDateAsync = async (isoDate) => {
  await getWorkingHours();
  return _lookupForDate(isoDate);
};

/** Admin-only modal: clock-in on behalf of an employee.
 * Server: POST /api/time-entries with { target_user_id, project_id, date, start_time, end_time, ... }
 * Status auto-set to 'approved' since admin already validated it.
 */
export const AdminPunchModal = ({ open, onClose, onCreated, employees, projects, defaultUserId = '' }) => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const initialDefaults = defaultHoursForDate(today);
  const [form, setForm] = useState({
    target_user_id: defaultUserId,
    project_id: '',
    date: today,
    start_time: initialDefaults.start_time,
    end_time: initialDefaults.end_time,
    break_duration: initialDefaults.break_duration,
    notes: '',
    location_address: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Load (or refresh) the configured working hours, then apply them.
      getWorkingHours().then(() => {
        const d = defaultHoursForDate(today);
        setForm(f => ({ ...f, target_user_id: defaultUserId || f.target_user_id || '', date: today, start_time: d.start_time, end_time: d.end_time, break_duration: d.break_duration }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultUserId]);

  const computedHours = (() => {
    try {
      const [h1, m1] = form.start_time.split(':').map(Number);
      const [h2, m2] = form.end_time.split(':').map(Number);
      const diffMin = (h2 * 60 + m2) - (h1 * 60 + m1) - Number(form.break_duration || 0);
      return diffMin > 0 ? (diffMin / 60).toFixed(2) : '0.00';
    } catch { return '0.00'; }
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.target_user_id) { toast.error('Selectionnez un salarie'); return; }
    if (!form.project_id) { toast.error('Selectionnez un chantier'); return; }
    setSaving(true);
    try {
      const payload = {
        target_user_id: form.target_user_id,
        project_id: form.project_id,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        break_duration: Number(form.break_duration) || 0,
        notes: form.notes,
        location_address: form.location_address,
      };
      const res = await axios.post(`${API_URL}/api/time-entries`, payload, { withCredentials: true });
      const targetName = (employees.find(e => e.id === form.target_user_id) || {}).name || 'salarie';
      toast.success(`Pointage cree pour ${targetName} (${computedHours}h, valide)`);
      onCreated && onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur creation pointage');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} data-testid="admin-punch-modal">
      <div style={{ background: 'white', borderRadius: 10, width: '95%', maxWidth: 540, padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <User size={18} weight="bold" /> Pointage a la place d'un salarie
          </h3>
          <button onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#5E5F66', margin: '0 0 16px 0', backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD', padding: '8px 10px', borderRadius: 6 }}>
          Le pointage sera automatiquement <strong>valide</strong> (puisque c'est vous qui le saisissez).
        </p>
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Salarie *</label>
            <select required className="form-input" value={form.target_user_id}
              onChange={(e) => setForm({ ...form, target_user_id: e.target.value })}
              data-testid="admin-punch-employee">
              <option value="">-- Selectionner --</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.name}{e.job_title ? ` (${e.job_title})` : ''}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Chantier *</label>
            <select required className="form-input" value={form.project_id}
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              data-testid="admin-punch-project">
              <option value="">-- Selectionner --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` - ${p.client_name}` : ''}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" required className="form-input" value={form.date}
                onChange={(e) => {
                  const newDate = e.target.value;
                  const d = defaultHoursForDate(newDate);
                  setForm(f => ({ ...f, date: newDate, start_time: d.start_time, end_time: d.end_time, break_duration: d.break_duration }));
                }}
                data-testid="admin-punch-date" />
            </div>
            <div className="form-group">
              <label className="form-label">Debut *</label>
              <input type="time" required className="form-input" value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid="admin-punch-start" />
            </div>
            <div className="form-group">
              <label className="form-label">Fin *</label>
              <input type="time" required className="form-input" value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                data-testid="admin-punch-end" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Pause (min)</label>
              <input type="number" min="0" step="5" className="form-input" value={form.break_duration}
                onChange={(e) => setForm({ ...form, break_duration: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Localisation (optionnel)</label>
              <input type="text" className="form-input" value={form.location_address}
                onChange={(e) => setForm({ ...form, location_address: e.target.value })}
                placeholder="Adresse / commune" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes (optionnel)</label>
            <textarea className="form-input" rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Detail du travail effectue" />
          </div>
          <div style={{ marginBottom: 12, padding: '6px 10px', backgroundColor: '#F4F5F7', borderRadius: 6, fontSize: '0.85rem', textAlign: 'center' }}>
            Total : <strong style={{ color: '#002FA7' }}>{computedHours} h</strong>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="admin-punch-save">
              <FloppyDisk size={14} /> {saving ? 'Enregistrement...' : 'Creer le pointage'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/** Compact button to trigger the modal. */
export const AdminPunchButton = ({ employees, projects, onCreated, defaultUserId = '', label = 'Pointer pour un salarie' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={() => setOpen(true)} data-testid="admin-punch-btn">
        <Plus size={14} weight="bold" /> {label}
      </button>
      <AdminPunchModal open={open} onClose={() => setOpen(false)} onCreated={onCreated}
        employees={employees} projects={projects} defaultUserId={defaultUserId} />
    </>
  );
};
